/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { memo, type ReactNode, useState } from 'react';
import {
  Chip,
  type ChipColorTypes,
  type ChipKindTypes,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../hooks';

import { CHIPS, CONFIG, KINDS, SIZES } from './constant';
import { getPercentage, pathForSemiCircularSvg } from './utils';

import './SodRiskScore.css';

interface SodRiskScoreProps {
  kind?: ChipColorTypes;
  size?: string;
  value?: number;
  label?: string;
  chipKind?: ChipKindTypes;
  className?: string;
  shouldShowZero?: boolean;
  customChip?: ReactNode;
}

function SodRiskScore({
  kind,
  size,
  value,
  label,
  chipKind,
  className,
  shouldShowZero,
  customChip,
}: SodRiskScoreProps) {
  const l = useInvertedLocalize();
  const displayLabel = label ?? l('None');
  const [svgDimensions] = useState(CONFIG[size ?? SIZES.small]);
  const [percentage] = useState(getPercentage(value, shouldShowZero));

  const progressStatsIconIndicator = cx(
    'Sod-RiskScore-progressStatsIconIndicator',
    `Sod-RiskScore-progressStatsIconIndicator--${kind ?? ''}`,
    className
  );

  const progressStatsNumberColor = cx(
    `Sod-RiskScore-progressStatsNumberColor--${kind ?? ''}`
  );

  const progressStatsLabelColor = cx(
    `Sod-RiskScore-progressLabel--${kind ?? ''}`
  );

  return (
    <div
      className={`Sod-RiskScore--${size ?? ''}${
        displayLabel.trim().length === 0 && size === SIZES.regular
          ? '--noLabel'
          : ''
      }`}>
      <div className='Sod-RiskScore-wrapper'>
        <div className='Sod-RiskScore-progressStats'>
          <div className='Sod-RiskScore-progressStatsIcon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={svgDimensions.width}
              height={svgDimensions.height}
              viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
              fill='none'>
              <path
                d={pathForSemiCircularSvg(
                  parseInt(String(svgDimensions.width / 2), 10),
                  parseInt(String(svgDimensions.height / 2), 10),
                  parseInt(
                    String(svgDimensions.width / 2 - svgDimensions.strokeWidth),
                    10
                  ),
                  -parseInt(String(svgDimensions.baseSpanInDegree / 2), 10),
                  parseInt(String(svgDimensions.baseSpanInDegree / 2), 10)
                )}
                fill='none'
                stroke='#EBECF2'
                strokeWidth={svgDimensions.strokeWidth}
                strokeLinecap='round'
              />
              {typeof percentage === 'number' && (
                <path
                  className={progressStatsIconIndicator}
                  d={pathForSemiCircularSvg(
                    parseInt(String(svgDimensions.width / 2), 10),
                    parseInt(String(svgDimensions.height / 2), 10),
                    parseInt(
                      String(
                        svgDimensions.width / 2 - svgDimensions.strokeWidth
                      ),
                      10
                    ),
                    -parseInt(String(svgDimensions.baseSpanInDegree / 2), 10),
                    -parseInt(String(svgDimensions.baseSpanInDegree / 2), 10) +
                      (percentage / 100) * svgDimensions.baseSpanInDegree
                  )}
                  fill='none'
                  stroke='#028329'
                  strokeWidth={svgDimensions.strokeWidth}
                  strokeLinecap='round'
                />
              )}
            </svg>
          </div>
          <div className='Sod-RiskScore-progressStatsNumber'>
            <Typography
              kind={size === SIZES.regular ? 'h2' : 'body2'}
              className={progressStatsNumberColor}>
              {percentage}
            </Typography>
          </div>
        </div>
        {size === SIZES.small && (
          <div className='Sod-RiskScore-progressChip--small'>
            <Typography kind='body3' className={progressStatsLabelColor}>
              {displayLabel}
            </Typography>
          </div>
        )}
        {!customChip &&
          size === SIZES.regular &&
          displayLabel &&
          displayLabel.trim().length > 0 && (
            <div className='Sod-RiskScore-progressChip--regular'>
              <Chip
                kind={chipKind}
                label={displayLabel}
                size='small'
                color={kind}
              />
            </div>
          )}
        {customChip}
      </div>
    </div>
  );
}

SodRiskScore.defaultProps = {
  kind: KINDS.info,
  size: SIZES.small,
  value: 0,
  label: undefined,
  chipKind: CHIPS.outlined,
  className: null,
  shouldShowZero: false,
  customChip: null,
};

export default memo(SodRiskScore);
